import { useCallback, useEffect, useState } from 'react';
import { api } from './api.js';

/**
 * The rest of the nutrient panel.
 *
 * The design problem here is not the numbers, it is the blanks. South African
 * labels print energy, protein, carbohydrate, sugar, fat, saturated fat, fibre
 * and sodium, and very little else. A screen that draws fourteen identical bars
 * and leaves nine of them at zero teaches the user that they eat no magnesium,
 * which is not what the data says. What the data says is that nobody printed it.
 *
 * So: every figure carries the share of the day's food it was computed from,
 * anything too thinly covered to judge is grouped separately and explained, and
 * the word "unknown" is used where it is the truth.
 */

interface Reading {
  key: string; label: string; unit: string; kind: 'goal' | 'limit';
  amount: number; coverage: number;
  goal: number | null; limit: number | null;
  status: 'unknown' | 'short' | 'on_track' | 'over' | 'well_over';
  detail: string;
}

interface Day {
  date: string; kcal: number;
  readings: Reading[];
  pinned: string[];
  sodiumPotassium: { ratio: number; detail: string } | null;
  caveat: string | null;
}

const UNIT: Record<string, string> = { g: 'g', mg: 'mg', ug: 'µg' };

const fmt = (v: number, unit: string): string =>
  `${v >= 100 ? Math.round(v) : Math.round(v * 10) / 10} ${UNIT[unit] ?? unit}`;

const TONE: Record<Reading['status'], string> = {
  unknown: 'muted',
  short: 'warn',
  on_track: 'good',
  over: 'warn',
  well_over: 'bad',
};

const WORD: Record<Reading['status'], string> = {
  unknown: 'Not enough label data',
  short: 'Short',
  on_track: 'On track',
  over: 'Over',
  well_over: 'Well over',
};

/**
 * The bar is drawn against the target, and the COVERED portion is drawn
 * differently from the rest. A single flat bar would imply the whole day is
 * accounted for, which is the exact impression this page exists to avoid.
 */
function Bar({ r }: { r: Reading }): JSX.Element | null {
  const target = r.kind === 'goal' ? r.goal : r.limit;
  if (target == null || target <= 0) return null;
  const filled = Math.min(100, (r.amount / target) * 100);
  const covered = Math.min(100, r.coverage * 100);
  return (
    <div className="bar">
      <span className={`bar-fill ${TONE[r.status]}`} style={{ width: `${filled}%` }} />
      {r.coverage < 0.95 && (
        <span className="bar-coverage" style={{ width: `${covered}%` }} title={`${Math.round(covered)}% of today's food lists this`} />
      )}
    </div>
  );
}

function Row({ r, onTarget }: { r: Reading; onTarget: (r: Reading) => void }): JSX.Element {
  const target = r.kind === 'goal' ? r.goal : r.limit;
  return (
    <div className="stack">
      <div className="row between">
        <strong>{r.label}</strong>
        <span className={TONE[r.status]}>
          {r.status === 'unknown' ? WORD[r.status] : `${fmt(r.amount, r.unit)}${target != null ? ` / ${fmt(target, r.unit)}` : ''}`}
        </span>
      </div>
      <Bar r={r} />
      <p className="muted small">{r.detail}</p>
      <button type="button" className="ghost" onClick={() => onTarget(r)}>
        {r.kind === 'goal' ? 'Set my own floor' : 'Set my own ceiling'}
      </button>
    </div>
  );
}

export function Nutrients(): JSX.Element {
  const [day, setDay] = useState<Day | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Reading | null>(null);
  const [amount, setAmount] = useState('');
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    try {
      setDay(await api<Day>('/nutrients/day'));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load nutrients.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const saveTarget = async (): Promise<void> => {
    if (!editing) return;
    try {
      await api<unknown>(`/nutrients/targets/${editing.key}`, {
        method: 'PUT',
        body: JSON.stringify({ amount: amount === '' ? null : Number(amount) }),
      });
      setEditing(null);
      setAmount('');
      void load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That target did not save.');
    }
  };

  if (error && !day) return <div className="card"><p className="bad">{error}</p></div>;
  if (!day) return <div className="card"><p className="muted small">Loading.</p></div>;

  const known = day.readings.filter((r) => r.status !== 'unknown');
  const unknown = day.readings.filter((r) => r.status === 'unknown');

  return (
    <>
      <div className="card">
        <h2>Nutrients today</h2>
        <p className="muted small">
          {day.kcal.toLocaleString()} kcal logged. Every figure below is summed only over the food
          that actually lists it, and says what share of the day that was.
        </p>
        {day.caveat && <p className="muted small">{day.caveat}</p>}
      </div>

      {day.sodiumPotassium && (
        <div className="card">
          <h2>Sodium against potassium</h2>
          <p className="big">{day.sodiumPotassium.ratio.toFixed(2)}</p>
          <p className="muted small">{day.sodiumPotassium.detail}</p>
          <p className="muted small">
            Shown only when both are well covered. A ratio built from two partial sums compounds
            both errors and looks more precise than either input.
          </p>
        </div>
      )}

      {known.length > 0 && (
        <div className="card">
          <h2>What today's food actually says</h2>
          {known.map((r) => <Row key={r.key} r={r} onTarget={(x) => { setEditing(x); setAmount(''); }} />)}
        </div>
      )}

      {unknown.length > 0 && (
        <div className="card">
          <h2>{unknown.length} nutrient{unknown.length === 1 ? '' : 's'} nothing today lists</h2>
          <p className="muted small">
            Blank because the labels do not print them, not because you ate none. Guessing a number
            here would be worse than the blank.
          </p>
          <button type="button" className="ghost" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Hide' : 'Show them'}
          </button>
          {showAll && unknown.map((r) => (
            <div key={r.key} className="row between">
              <span>{r.label}</span>
              <span className="muted small">{r.detail}</span>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="card">
          <h2>
            Your own {editing.kind === 'goal' ? 'floor' : 'ceiling'} for {editing.label}
          </h2>
          <label className="field">
            <span>
              Amount in {UNIT[editing.unit] ?? editing.unit}, or blank to go back to the guidance
            </span>
            <input type="number" step="0.1" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>
          <div className="row">
            <button type="button" onClick={() => void saveTarget()}>Save</button>
            <button type="button" className="ghost" onClick={() => setEditing(null)}>Cancel</button>
          </div>
          <p className="muted small">
            {editing.kind === 'goal'
              ? 'A floor to clear. Being under it is the thing worth knowing about.'
              : 'A ceiling to stay under. Being under it is fine, and the app will not push you toward it.'}
          </p>
        </div>
      )}

      {error && <p className="bad">{error}</p>}
    </>
  );
}
